import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../../api';

const CATEGORY_LABELS = {
    'path-to-the-robe': 'The Path to the Robe',
    'as-it-was-taught-to-me': 'As It Was Taught to Me',
};

const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
        return new Date(isoString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return isoString;
    }
};

const authorDisplay = (author) => {
    if (author === 'brandon') return 'Brandon Williams';
    if (author === 'logan') return 'Logan Schmidt (林和正)';
    return author;
};

const BlogCategory = () => {
    const { category } = useParams();
    const navigate = useNavigate();
    const categoryLabel = CATEGORY_LABELS[category];

    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [lastKey, setLastKey] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    const fetchPosts = useCallback(async (paginationKey = null) => {
        const isInitial = !paginationKey;
        if (isInitial) setIsLoading(true);
        else setIsLoadingMore(true);
        setError(null);

        try {
            const params = new URLSearchParams({ category, limit: '10' });
            if (paginationKey) params.set('lastEvaluatedKey', JSON.stringify(paginationKey));

            const res = await fetch(`${API_URL}/blog?${params.toString()}`);
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            const data = await res.json();
            const newPosts = data.posts || [];

            if (isInitial) {
                setPosts(newPosts);
            } else {
                setPosts((prev) => [...prev, ...newPosts]);
            }

            setLastKey(data.lastEvaluatedKey || null);
            setHasMore(!!data.lastEvaluatedKey);
        } catch (err) {
            setError('Unable to load posts at this time.');
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [category]);

    useEffect(() => {
        setPosts([]);
        setLastKey(null);
        setHasMore(false);
        fetchPosts();
    }, [fetchPosts]);

    if (!categoryLabel) {
        return (
            <div className="min-h-screen py-12 px-6">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-10 text-center">
                        <p className="text-white text-lg mb-4">Category not found.</p>
                        <Link to="/blog" className="text-amber-400 hover:underline text-sm">
                            Back to Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-3xl mx-auto">
                {/* Breadcrumb */}
                <nav className="mb-6 text-sm text-gray-400" aria-label="Breadcrumb">
                    <Link to="/blog" className="hover:text-amber-400 transition-colors">Blog</Link>
                    <span className="mx-2 text-gray-600">&rsaquo;</span>
                    <span className="text-gray-200">{categoryLabel}</span>
                </nav>

                {/* Header */}
                <div className="mb-8">
                    <div className="inline-block bg-slate-900/70 backdrop-blur-sm rounded-xl px-8 py-6">
                        <h1 className="text-3xl font-bold text-white mb-1">{categoryLabel}</h1>
                    </div>
                </div>

                {/* Loading */}
                {isLoading && (
                    <div className="flex justify-center py-16">
                        <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                    </div>
                )}

                {/* Error */}
                {!isLoading && error && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-8 text-center">
                        <p className="text-gray-400">{error}</p>
                    </div>
                )}

                {/* Empty */}
                {!isLoading && !error && posts.length === 0 && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-10 text-center">
                        <p className="text-gray-500 italic">
                            Coming soon &mdash; writings will appear here.
                        </p>
                    </div>
                )}

                {/* Post list */}
                {!isLoading && !error && posts.length > 0 && (
                    <div className="space-y-4">
                        {posts.map((post) => (
                            <button
                                key={post.slug || post.id}
                                onClick={() => navigate(`/blog/post/${post.slug}`)}
                                className="text-left w-full bg-slate-900/70 backdrop-blur-sm border border-slate-700 hover:border-amber-500/50 rounded-xl p-5 flex flex-col gap-2 transition-colors"
                            >
                                <h2 className="text-white font-semibold text-lg leading-snug">{post.title}</h2>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>{authorDisplay(post.author)}</span>
                                    {(post.publishedAt || post.createdAt) && (
                                        <>
                                            <span>&middot;</span>
                                            <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                                        </>
                                    )}
                                </div>
                                {post.excerpt && (
                                    <p className="text-gray-400 text-sm leading-relaxed">{post.excerpt}</p>
                                )}
                            </button>
                        ))}

                        {/* Load more */}
                        {hasMore && (
                            <div className="text-center pt-4">
                                <button
                                    onClick={() => fetchPosts(lastKey)}
                                    disabled={isLoadingMore}
                                    className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-white text-sm px-6 py-2 rounded-full transition-colors"
                                >
                                    {isLoadingMore ? 'Loading...' : 'Load more'}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogCategory;
